import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { cn } from "../../lib/utils";

/**
 * Horizontal scroll-snap pager. Each child becomes a 100%-wide pane with
 * `scroll-snap-align: start`. Native scrolling gives us free momentum and
 * touch handling on Android/Chrome — no JS gesture library required.
 *
 * `onPaneChange` fires with the new index once a snap settles.
 *
 * Exposes an imperative `{ goTo, activeIndex }` handle via ref so a parent can
 * *nudge* the visible pane (e.g. auto-follow the workout phase). It's a
 * one-shot nudge, not a controlled prop, so it never fights the user's own
 * swipe between nudges.
 */
const SwipeablePanes = forwardRef(function SwipeablePanes(
  {
    children,
    className,
    paneClassName,
    onPaneChange,
    showIndicators = true,
    indicatorLabels,
  },
  ref,
) {
  const scrollerRef = useRef(null);
  const panes = React.Children.toArray(children).filter(Boolean);
  const [activeIndex, setActiveIndex] = useState(0);
  const rafRef = useRef(0);

  const recompute = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const width = el.clientWidth || 1;
    const index = Math.round(el.scrollLeft / width);
    setActiveIndex((prev) => {
      if (prev === index) return prev;
      onPaneChange?.(index);
      return index;
    });
  }, [onPaneChange]);

  const onScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      recompute();
    });
  }, [recompute]);

  useEffect(() => {
    recompute();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [recompute, panes.length]);

  const goTo = useCallback((i) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  }, []);

  useImperativeHandle(ref, () => ({ goTo, activeIndex }), [goTo, activeIndex]);

  // The indicator tabs float over the panes (absolute, bottom-2, z-20), so
  // anything a pane puts at its own bottom edge ends up underneath them — the
  // Coach pane's pinned Stop Workout row, for one, which grew when the live
  // session actions joined it. Reserve the strip rather than leaving each pane
  // to remember it.
  const hasIndicators = showIndicators && panes.length > 1;

  return (
    <div className={cn("relative w-full h-full", className)}>
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="flex h-full w-full overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden"
        // `touch-action: pan-x` tells the browser this scroller owns horizontal
        // panning, so it doesn't have to disambiguate against the nested
        // vertical scrollers on every gesture — that guesswork is what made
        // swiping feel unresponsive. Each pane re-claims vertical panning below.
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          touchAction: "pan-x",
        }}
      >
        {panes.map((child, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className={cn(
              "flex-shrink-0 w-full h-full snap-start overflow-y-auto",
              hasIndicators && "pb-12",
              paneClassName,
            )}
            style={{ touchAction: "pan-y" }}
            aria-hidden={i !== activeIndex}
          >
            {child}
          </div>
        ))}
      </div>

      {hasIndicators && (
        // Labelled pill tabs (when labels are supplied) double as a clear,
        // tappable affordance — the old 1.5 px dots were easy to miss and hard
        // to hit. Falls back to dots when no labels are provided.
        <div className="pointer-events-none absolute inset-x-0 bottom-2 z-20 flex justify-center">
          <div className="pointer-events-auto flex items-center gap-1 rounded-full bg-black/40 p-1 backdrop-blur-sm">
            {panes.map((_, i) => {
              const label = indicatorLabels?.[i];
              const active = i === activeIndex;
              return (
                <button
                  type="button"
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={label ? `Go to ${label}` : `Pane ${i + 1}`}
                  aria-current={active ? "true" : undefined}
                  className={cn(
                    "rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    label
                      ? cn(
                          "px-3 py-1 text-xs font-medium",
                          active
                            ? "bg-white/85 text-black"
                            : "text-white/60 hover:text-white",
                        )
                      : cn(
                          "h-2",
                          active
                            ? "w-6 bg-white/85"
                            : "w-2 bg-white/30 hover:bg-white/55",
                        ),
                  )}
                >
                  {label || null}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

export default SwipeablePanes;
